/**
 * Centralized Rules Engine for German License Plates
 * 
 * This engine is completely data-driven - no hardcoded logic in UI components.
 * All validation is computed dynamically from the rules database.
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type VehicleCategory =
  | 'PKW'           // Personenkraftwagen (Car)
  | 'MOTORCYCLE'    // Motorrad
  | 'TRUCK'         // Lastkraftwagen (Truck)
  | 'TRAILER'       // Anhänger
  | 'LEICHTKRAFT'   // Leichtkraftrad (125cc)
  | 'TRAKTOR'       // Traktor (Tractor)

export type PlateLayout = 'einzeilig' | 'zweizeilig'

export type PlateType =
  | 'REGULAR'
  | 'REGULAR_SEASON'
  | 'E'
  | 'E_SEASON'
  | 'H'
  | 'H_SEASON'
  | 'GREEN'
  | 'SHORT_TERM'
  | 'EXPORT'

export interface PlateSize {
  width: number  // mm
  height: number // mm
  layout: PlateLayout
}

export interface LetterNumberCombination {
  letters: number  // Number of letters after city code (1-2)
  numbers: number  // Number of numbers after letters (1-4)
}

/**
 * City-code-dependent combinations
 * Some rules depend on the city code length (1, 2, or 3 letters)
 */
export interface CityCodeDependentCombinations {
  cityCodeLength1?: LetterNumberCombination[]  // Rules when city code = 1 letter
  cityCodeLength2?: LetterNumberCombination[]  // Rules when city code = 2 letters
  cityCodeLength3?: LetterNumberCombination[]  // Rules when city code = 3 letters
}

export interface PlateRule {
  // Identification
  vehicleCategory: VehicleCategory
  plateSize: PlateSize
  plateType: PlateType

  // Limits
  maxTotalChars: number
  maxCityCodeLength: number
  minCityCodeLength: number

  // Allowed combinations - can be simple or city-code-dependent
  allowedCombinations?: LetterNumberCombination[]  // Simple: same for all city code lengths
  cityCodeDependentCombinations?: CityCodeDependentCombinations  // Complex: depends on city code length

  // Options
  allowsTwoLine: boolean
  seasonalMarkersCount: boolean // Whether E/H markers count toward limit (they NEVER reduce it)

  // Metadata
  description?: string
}

// ============================================================================
// RULES DATABASE
// ============================================================================

/**
 * Central rules database - all plate configurations are defined here.
 * Add new rules by adding objects to this array.
 */
const PLATE_RULES: PlateRule[] = [
  // ============================================================================
  // PKW & LKW - Standard Sizes (EXACT RULES)
  // ============================================================================

  // PKW/LKW - 110 × 520 mm
  // Max: 8 characters, ALL combinations allowed
  {
    vehicleCategory: 'PKW',
    plateSize: { width: 520, height: 110, layout: 'einzeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 8,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    allowedCombinations: [
      { letters: 1, numbers: 1 },
      { letters: 1, numbers: 2 },
      { letters: 1, numbers: 3 },
      { letters: 1, numbers: 4 },
      { letters: 2, numbers: 1 },
      { letters: 2, numbers: 2 },
      { letters: 2, numbers: 3 },
      { letters: 2, numbers: 4 },
    ],
    allowsTwoLine: false,
    seasonalMarkersCount: false,
    description: '110 × 520 mm - Max 8 chars, all combinations allowed',
  },

  // LKW - 110 × 520 mm (same as PKW)
  {
    vehicleCategory: 'TRUCK',
    plateSize: { width: 520, height: 110, layout: 'einzeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 8,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    allowedCombinations: [
      { letters: 1, numbers: 1 },
      { letters: 1, numbers: 2 },
      { letters: 1, numbers: 3 },
      { letters: 1, numbers: 4 },
      { letters: 2, numbers: 1 },
      { letters: 2, numbers: 2 },
      { letters: 2, numbers: 3 },
      { letters: 2, numbers: 4 },
    ],
    allowsTwoLine: false,
    seasonalMarkersCount: false,
    description: '110 × 520 mm - Max 8 chars, all combinations allowed',
  },

  // PKW/LKW - 110 × 480 mm
  // Max: 6 characters
  // If city code = 3 letters: Either 2 letters + 1 number OR 1 letter + 2 numbers
  // Simplified: 2 letters after city → only 1 number, 1 letter after city → up to 2 numbers
  {
    vehicleCategory: 'PKW',
    plateSize: { width: 480, height: 110, layout: 'einzeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 6,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    cityCodeDependentCombinations: {
      cityCodeLength1: [
        { letters: 1, numbers: 1 },
        { letters: 1, numbers: 2 },
        { letters: 2, numbers: 1 },
      ],
      cityCodeLength2: [
        { letters: 1, numbers: 1 },
        { letters: 1, numbers: 2 },
        { letters: 2, numbers: 1 },
      ],
      cityCodeLength3: [
        { letters: 2, numbers: 1 },  // 2 letters + 1 number
        { letters: 1, numbers: 2 },  // 1 letter + 2 numbers
      ],
    },
    allowsTwoLine: false,
    seasonalMarkersCount: false,
    description: '110 × 480 mm - Max 6 chars, complex rules for 3-letter city codes',
  },

  // LKW - 110 × 480 mm (same as PKW)
  {
    vehicleCategory: 'TRUCK',
    plateSize: { width: 480, height: 110, layout: 'einzeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 6,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    cityCodeDependentCombinations: {
      cityCodeLength1: [
        { letters: 1, numbers: 1 },
        { letters: 1, numbers: 2 },
        { letters: 2, numbers: 1 },
      ],
      cityCodeLength2: [
        { letters: 1, numbers: 1 },
        { letters: 1, numbers: 2 },
        { letters: 2, numbers: 1 },
      ],
      cityCodeLength3: [
        { letters: 2, numbers: 1 },
        { letters: 1, numbers: 2 },
      ],
    },
    allowsTwoLine: false,
    seasonalMarkersCount: false,
    description: '110 × 480 mm - Max 6 chars, complex rules for 3-letter city codes',
  },

  // PKW/LKW - 110 × 460 mm
  // Same rules as 480 mm
  {
    vehicleCategory: 'PKW',
    plateSize: { width: 460, height: 110, layout: 'einzeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 6,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    cityCodeDependentCombinations: {
      cityCodeLength1: [
        { letters: 1, numbers: 1 },
        { letters: 1, numbers: 2 },
        { letters: 2, numbers: 1 },
      ],
      cityCodeLength2: [
        { letters: 1, numbers: 1 },
        { letters: 1, numbers: 2 },
        { letters: 2, numbers: 1 },
      ],
      cityCodeLength3: [
        { letters: 2, numbers: 1 },
        { letters: 1, numbers: 2 },
      ],
    },
    allowsTwoLine: false,
    seasonalMarkersCount: false,
    description: '110 × 460 mm - Same rules as 480 mm',
  },

  // LKW - 110 × 460 mm (same as PKW)
  {
    vehicleCategory: 'TRUCK',
    plateSize: { width: 460, height: 110, layout: 'einzeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 6,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    cityCodeDependentCombinations: {
      cityCodeLength1: [
        { letters: 1, numbers: 1 },
        { letters: 1, numbers: 2 },
        { letters: 2, numbers: 1 },
      ],
      cityCodeLength2: [
        { letters: 1, numbers: 1 },
        { letters: 1, numbers: 2 },
        { letters: 2, numbers: 1 },
      ],
      cityCodeLength3: [
        { letters: 2, numbers: 1 },
        { letters: 1, numbers: 2 },
      ],
    },
    allowsTwoLine: false,
    seasonalMarkersCount: false,
    description: '110 × 460 mm - Same rules as 480 mm',
  },

  // PKW/LKW - 110 × 420 mm
  // Max: 5 characters
  // City code of 3 letters allowed ONLY if: 1 letter + 1 number
  {
    vehicleCategory: 'PKW',
    plateSize: { width: 420, height: 110, layout: 'einzeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 5,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    cityCodeDependentCombinations: {
      cityCodeLength1: [
        { letters: 1, numbers: 1 },
        { letters: 1, numbers: 2 },
        { letters: 1, numbers: 3 },
        { letters: 2, numbers: 1 },
        { letters: 2, numbers: 2 },
      ],
      cityCodeLength2: [
        { letters: 1, numbers: 1 },
        { letters: 1, numbers: 2 },
        { letters: 2, numbers: 1 },
      ],
      cityCodeLength3: [
        { letters: 1, numbers: 1 },  // ONLY this combination allowed for 3-letter city codes
      ],
    },
    allowsTwoLine: false,
    seasonalMarkersCount: false,
    description: '110 × 420 mm - Max 5 chars, 3-letter city codes only with 1 letter + 1 number',
  },

  // LKW - 110 × 420 mm (same as PKW)
  {
    vehicleCategory: 'TRUCK',
    plateSize: { width: 420, height: 110, layout: 'einzeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 5,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    cityCodeDependentCombinations: {
      cityCodeLength1: [
        { letters: 1, numbers: 1 },
        { letters: 1, numbers: 2 },
        { letters: 1, numbers: 3 },
        { letters: 2, numbers: 1 },
        { letters: 2, numbers: 2 },
      ],
      cityCodeLength2: [
        { letters: 1, numbers: 1 },
        { letters: 1, numbers: 2 },
        { letters: 2, numbers: 1 },
      ],
      cityCodeLength3: [
        { letters: 1, numbers: 1 },
      ],
    },
    allowsTwoLine: false,
    seasonalMarkersCount: false,
    description: '110 × 420 mm - Max 5 chars, 3-letter city codes only with 1 letter + 1 number',
  },

  // PKW/LKW - 110 × 400 mm
  // Max: 4 characters
  // City codes with 3 letters NOT allowed
  {
    vehicleCategory: 'PKW',
    plateSize: { width: 400, height: 110, layout: 'einzeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 4,
    maxCityCodeLength: 2,  // NO 3-letter city codes allowed
    minCityCodeLength: 1,
    cityCodeDependentCombinations: {
      cityCodeLength1: [
        { letters: 1, numbers: 1 },
        { letters: 1, numbers: 2 },
        { letters: 1, numbers: 3 },
        { letters: 2, numbers: 1 },
      ],
      cityCodeLength2: [
        { letters: 1, numbers: 1 },
        { letters: 2, numbers: 1 },
      ],
    },
    allowsTwoLine: false,
    seasonalMarkersCount: false,
    description: '110 × 400 mm - Max 4 chars, NO 3-letter city codes allowed',
  },

  // LKW - 110 × 400 mm (same as PKW)
  {
    vehicleCategory: 'TRUCK',
    plateSize: { width: 400, height: 110, layout: 'einzeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 4,
    maxCityCodeLength: 2,
    minCityCodeLength: 1,
    cityCodeDependentCombinations: {
      cityCodeLength1: [
        { letters: 1, numbers: 1 },
        { letters: 1, numbers: 2 },
        { letters: 1, numbers: 3 },
        { letters: 2, numbers: 1 },
      ],
      cityCodeLength2: [
        { letters: 1, numbers: 1 },
        { letters: 2, numbers: 1 },
      ],
    },
    allowsTwoLine: false,
    seasonalMarkersCount: false,
    description: '110 × 400 mm - Max 4 chars, NO 3-letter city codes allowed',
  },

  // ============================================================================
  // TRAILER (Anhänger) - Same rules as PKW/LKW
  // ============================================================================

  // TRAILER - 110 × 520 mm
  {
    vehicleCategory: 'TRAILER',
    plateSize: { width: 520, height: 110, layout: 'einzeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 8,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    allowedCombinations: [
      { letters: 1, numbers: 1 },
      { letters: 1, numbers: 2 },
      { letters: 1, numbers: 3 },
      { letters: 1, numbers: 4 },
      { letters: 2, numbers: 1 },
      { letters: 2, numbers: 2 },
      { letters: 2, numbers: 3 },
      { letters: 2, numbers: 4 },
    ],
    allowsTwoLine: false,
    seasonalMarkersCount: false,
    description: '110 × 520 mm - Max 8 chars, all combinations allowed',
  },

  // TRAILER - 110 × 480 mm
  {
    vehicleCategory: 'TRAILER',
    plateSize: { width: 480, height: 110, layout: 'einzeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 6,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    cityCodeDependentCombinations: {
      cityCodeLength1: [
        { letters: 1, numbers: 1 },
        { letters: 1, numbers: 2 },
        { letters: 2, numbers: 1 },
      ],
      cityCodeLength2: [
        { letters: 1, numbers: 1 },
        { letters: 1, numbers: 2 },
        { letters: 2, numbers: 1 },
      ],
      cityCodeLength3: [
        { letters: 2, numbers: 1 },
        { letters: 1, numbers: 2 },
      ],
    },
    allowsTwoLine: false,
    seasonalMarkersCount: false,
    description: '110 × 480 mm - Max 6 chars, complex rules for 3-letter city codes',
  },

  // TRAILER - 110 × 460 mm
  {
    vehicleCategory: 'TRAILER',
    plateSize: { width: 460, height: 110, layout: 'einzeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 6,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    cityCodeDependentCombinations: {
      cityCodeLength1: [
        { letters: 1, numbers: 1 },
        { letters: 1, numbers: 2 },
        { letters: 2, numbers: 1 },
      ],
      cityCodeLength2: [
        { letters: 1, numbers: 1 },
        { letters: 1, numbers: 2 },
        { letters: 2, numbers: 1 },
      ],
      cityCodeLength3: [
        { letters: 2, numbers: 1 },
        { letters: 1, numbers: 2 },
      ],
    },
    allowsTwoLine: false,
    seasonalMarkersCount: false,
    description: '110 × 460 mm - Same rules as 480 mm',
  },

  // TRAILER - 110 × 420 mm
  {
    vehicleCategory: 'TRAILER',
    plateSize: { width: 420, height: 110, layout: 'einzeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 5,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    cityCodeDependentCombinations: {
      cityCodeLength1: [
        { letters: 1, numbers: 1 },
        { letters: 1, numbers: 2 },
        { letters: 1, numbers: 3 },
        { letters: 2, numbers: 1 },
        { letters: 2, numbers: 2 },
      ],
      cityCodeLength2: [
        { letters: 1, numbers: 1 },
        { letters: 1, numbers: 2 },
        { letters: 2, numbers: 1 },
      ],
      cityCodeLength3: [
        { letters: 1, numbers: 1 },
      ],
    },
    allowsTwoLine: false,
    seasonalMarkersCount: false,
    description: '110 × 420 mm - Max 5 chars, 3-letter city codes only with 1 letter + 1 number',
  },

  // TRAILER - 110 × 400 mm
  {
    vehicleCategory: 'TRAILER',
    plateSize: { width: 400, height: 110, layout: 'einzeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 4,
    maxCityCodeLength: 2,
    minCityCodeLength: 1,
    cityCodeDependentCombinations: {
      cityCodeLength1: [
        { letters: 1, numbers: 1 },
        { letters: 1, numbers: 2 },
        { letters: 1, numbers: 3 },
        { letters: 2, numbers: 1 },
      ],
      cityCodeLength2: [
        { letters: 1, numbers: 1 },
        { letters: 2, numbers: 1 },
      ],
    },
    allowsTwoLine: false,
    seasonalMarkersCount: false,
    description: '110 × 400 mm - Max 4 chars, NO 3-letter city codes allowed',
  },

  // ============================================================================
  // Two-line Plates (280-340 × 200 mm)
  // Max: 8 characters, city code up to 3 letters, remaining characters flexible
  // Seasonal plates (E/H) do NOT reduce allowed count
  // ============================================================================

  // Two-line - 340 × 200 mm
  {
    vehicleCategory: 'PKW',
    plateSize: { width: 340, height: 200, layout: 'zweizeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 8,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    allowedCombinations: [
      { letters: 1, numbers: 1 },
      { letters: 1, numbers: 2 },
      { letters: 1, numbers: 3 },
      { letters: 1, numbers: 4 },
      { letters: 2, numbers: 1 },
      { letters: 2, numbers: 2 },
      { letters: 2, numbers: 3 },
      { letters: 2, numbers: 4 },
    ],
    allowsTwoLine: true,
    seasonalMarkersCount: false,
    description: 'Two-line plate 340 × 200 mm - Max 8 chars, flexible combinations',
  },

  // Two-line - 320 × 200 mm
  {
    vehicleCategory: 'PKW',
    plateSize: { width: 320, height: 200, layout: 'zweizeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 8,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    allowedCombinations: [
      { letters: 1, numbers: 1 },
      { letters: 1, numbers: 2 },
      { letters: 1, numbers: 3 },
      { letters: 1, numbers: 4 },
      { letters: 2, numbers: 1 },
      { letters: 2, numbers: 2 },
      { letters: 2, numbers: 3 },
      { letters: 2, numbers: 4 },
    ],
    allowsTwoLine: true,
    seasonalMarkersCount: false,
    description: 'Two-line plate 320 × 200 mm - Max 8 chars, flexible combinations',
  },

  // Two-line - 280 × 200 mm
  {
    vehicleCategory: 'PKW',
    plateSize: { width: 280, height: 200, layout: 'zweizeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 8,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    allowedCombinations: [
      { letters: 1, numbers: 1 },
      { letters: 1, numbers: 2 },
      { letters: 1, numbers: 3 },
      { letters: 1, numbers: 4 },
      { letters: 2, numbers: 1 },
      { letters: 2, numbers: 2 },
      { letters: 2, numbers: 3 },
      { letters: 2, numbers: 4 },
    ],
    allowsTwoLine: true,
    seasonalMarkersCount: false,
    description: 'Two-line plate 280 × 200 mm - Max 8 chars, flexible combinations',
  },

  // Motorcycle - Two-line plates (same rules)
  {
    vehicleCategory: 'MOTORCYCLE',
    plateSize: { width: 340, height: 200, layout: 'zweizeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 8,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    allowedCombinations: [
      { letters: 1, numbers: 1 },
      { letters: 1, numbers: 2 },
      { letters: 1, numbers: 3 },
      { letters: 1, numbers: 4 },
      { letters: 2, numbers: 1 },
      { letters: 2, numbers: 2 },
      { letters: 2, numbers: 3 },
      { letters: 2, numbers: 4 },
    ],
    allowsTwoLine: true,
    seasonalMarkersCount: false,
    description: 'Motorcycle two-line plate - Max 8 chars',
  },

  {
    vehicleCategory: 'MOTORCYCLE',
    plateSize: { width: 320, height: 200, layout: 'zweizeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 8,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    allowedCombinations: [
      { letters: 1, numbers: 1 },
      { letters: 1, numbers: 2 },
      { letters: 1, numbers: 3 },
      { letters: 1, numbers: 4 },
      { letters: 2, numbers: 1 },
      { letters: 2, numbers: 2 },
      { letters: 2, numbers: 3 },
      { letters: 2, numbers: 4 },
    ],
    allowsTwoLine: true,
    seasonalMarkersCount: false,
    description: 'Motorcycle two-line plate - Max 8 chars',
  },

  {
    vehicleCategory: 'MOTORCYCLE',
    plateSize: { width: 280, height: 200, layout: 'zweizeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 8,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    allowedCombinations: [
      { letters: 1, numbers: 1 },
      { letters: 1, numbers: 2 },
      { letters: 1, numbers: 3 },
      { letters: 1, numbers: 4 },
      { letters: 2, numbers: 1 },
      { letters: 2, numbers: 2 },
      { letters: 2, numbers: 3 },
      { letters: 2, numbers: 4 },
    ],
    allowsTwoLine: true,
    seasonalMarkersCount: false,
    description: 'Motorcycle two-line plate - Max 8 chars',
  },

  // ============================================================================
  // MOTORCYCLE - Single-line Plates (Einzeilig)
  // ============================================================================

  // Motorcycle - 200 × 220 mm (Single-line) - Tall and narrow
  {
    vehicleCategory: 'MOTORCYCLE',
    plateSize: { width: 200, height: 220, layout: 'einzeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 5,
    maxCityCodeLength: 2,
    minCityCodeLength: 1,
    allowedCombinations: [
      { letters: 1, numbers: 1 },
      { letters: 1, numbers: 2 },
      { letters: 1, numbers: 3 },
      { letters: 2, numbers: 1 },
      { letters: 2, numbers: 2 },
    ],
    allowsTwoLine: false,
    seasonalMarkersCount: false,
    description: 'Motorcycle single-line plate 200 × 220 mm',
  },

  // Motorcycle - 200 × 200 mm (Single-line)
  {
    vehicleCategory: 'MOTORCYCLE',
    plateSize: { width: 200, height: 200, layout: 'einzeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 5,
    maxCityCodeLength: 2,
    minCityCodeLength: 1,
    allowedCombinations: [
      { letters: 1, numbers: 1 },
      { letters: 1, numbers: 2 },
      { letters: 1, numbers: 3 },
      { letters: 2, numbers: 1 },
      { letters: 2, numbers: 2 },
    ],
    allowsTwoLine: false,
    seasonalMarkersCount: false,
    description: 'Motorcycle single-line plate 200 × 200 mm',
  },

  // Motorcycle - 200 × 180 mm (Single-line) - Smallest
  {
    vehicleCategory: 'MOTORCYCLE',
    plateSize: { width: 200, height: 180, layout: 'einzeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 4,
    maxCityCodeLength: 2,
    minCityCodeLength: 1,
    cityCodeDependentCombinations: {
      cityCodeLength1: [
        { letters: 1, numbers: 1 },
        { letters: 1, numbers: 2 },
        { letters: 2, numbers: 1 },
      ],
      cityCodeLength2: [
        { letters: 1, numbers: 1 },
        { letters: 2, numbers: 1 },
      ],
    },
    allowsTwoLine: false,
    seasonalMarkersCount: false,
    description: 'Motorcycle single-line plate 200 × 180 mm',
  },

  // ============================================================================
  // LEICHTKRAFT (125cc) - 130 × 255 mm
  // ============================================================================

  {
    vehicleCategory: 'LEICHTKRAFT',
    plateSize: { width: 130, height: 255, layout: 'einzeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 8,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    allowedCombinations: [
      { letters: 1, numbers: 1 },
      { letters: 1, numbers: 2 },
      { letters: 1, numbers: 3 },
      { letters: 1, numbers: 4 },
      { letters: 2, numbers: 1 },
      { letters: 2, numbers: 2 },
      { letters: 2, numbers: 3 },
      { letters: 2, numbers: 4 },
    ],
    allowsTwoLine: false,
    seasonalMarkersCount: false,
    description: 'Leichtkraftrad 125cc plate',
  },

  // ============================================================================
  // TRAKTOR - 130 × 240 mm
  // ============================================================================

  {
    vehicleCategory: 'TRAKTOR',
    plateSize: { width: 130, height: 240, layout: 'einzeilig' },
    plateType: 'REGULAR',
    maxTotalChars: 7,
    maxCityCodeLength: 2,
    minCityCodeLength: 1,
    allowedCombinations: [
      { letters: 1, numbers: 3 },
      { letters: 1, numbers: 4 },
      { letters: 2, numbers: 3 },
      { letters: 2, numbers: 4 },
    ],
    allowsTwoLine: false,
    seasonalMarkersCount: false,
    description: 'Traktor (Landwirtschaft) plate',
  },

  // ============================================================================
  // Seasonal and Special Types (inherit from REGULAR but with markers)
  // ============================================================================

  // Note: E, H, and seasonal plates use the same rules as REGULAR
  // The markers (E, H, season dates) do NOT count toward character limits
  // We duplicate rules for each plate type to make queries easier

  // PKW - 520 × 110 mm - E-Kennzeichen
  {
    vehicleCategory: 'PKW',
    plateSize: { width: 520, height: 110, layout: 'einzeilig' },
    plateType: 'E',
    maxTotalChars: 8,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    allowedCombinations: [
      { letters: 1, numbers: 1 },
      { letters: 1, numbers: 2 },
      { letters: 1, numbers: 3 },
      { letters: 1, numbers: 4 },
      { letters: 2, numbers: 1 },
      { letters: 2, numbers: 2 },
      { letters: 2, numbers: 3 },
      { letters: 2, numbers: 4 },
    ],
    allowsTwoLine: false,
    seasonalMarkersCount: false, // E marker does NOT count toward limit
    description: 'E-Kennzeichen - same rules as REGULAR, E marker does not count',
  },

  // PKW - 520 × 110 mm - H-Kennzeichen
  {
    vehicleCategory: 'PKW',
    plateSize: { width: 520, height: 110, layout: 'einzeilig' },
    plateType: 'H',
    maxTotalChars: 8,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    allowedCombinations: [
      { letters: 1, numbers: 1 },
      { letters: 1, numbers: 2 },
      { letters: 1, numbers: 3 },
      { letters: 1, numbers: 4 },
      { letters: 2, numbers: 1 },
      { letters: 2, numbers: 2 },
      { letters: 2, numbers: 3 },
      { letters: 2, numbers: 4 },
    ],
    allowsTwoLine: false,
    seasonalMarkersCount: false, // H marker does NOT count toward limit
    description: 'H-Kennzeichen - same rules as REGULAR, H marker does not count',
  },

  // PKW - 520 × 110 mm - Saisonkennzeichen
  {
    vehicleCategory: 'PKW',
    plateSize: { width: 520, height: 110, layout: 'einzeilig' },
    plateType: 'REGULAR_SEASON',
    maxTotalChars: 8,
    maxCityCodeLength: 3,
    minCityCodeLength: 1,
    allowedCombinations: [
      { letters: 1, numbers: 1 },
      { letters: 1, numbers: 2 },
      { letters: 1, numbers: 3 },
      { letters: 1, numbers: 4 },
      { letters: 2, numbers: 1 },
      { letters: 2, numbers: 2 },
      { letters: 2, numbers: 3 },
      { letters: 2, numbers: 4 },
    ],
    allowsTwoLine: false,
    seasonalMarkersCount: false, // Season dates do NOT count toward limit
    description: 'Saisonkennzeichen - same rules as REGULAR, season dates do not count',
  },
]

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Find matching rule for given parameters
 */
export function findRule(
  vehicleCategory: VehicleCategory,
  plateSize: PlateSize,
  plateType: PlateType
): PlateRule | null {
  // First try exact match
  let rule = PLATE_RULES.find(
    r =>
      r.vehicleCategory === vehicleCategory &&
      r.plateSize.width === plateSize.width &&
      r.plateSize.height === plateSize.height &&
      r.plateSize.layout === plateSize.layout &&
      r.plateType === plateType
  )

  // If not found and plate type is seasonal/special, try with REGULAR
  if (!rule && plateType !== 'REGULAR') {
    rule = PLATE_RULES.find(
      r =>
        r.vehicleCategory === vehicleCategory &&
        r.plateSize.width === plateSize.width &&
        r.plateSize.height === plateSize.height &&
        r.plateSize.layout === plateSize.layout &&
        r.plateType === 'REGULAR'
    )
  }

  return rule || null
}

/**
 * Get all rules for a vehicle category
 */
export function getRulesForVehicle(vehicleCategory: VehicleCategory): PlateRule[] {
  return PLATE_RULES.filter(r => r.vehicleCategory === vehicleCategory)
}

/**
 * Get all rules for a plate size
 */
export function getRulesForSize(plateSize: PlateSize): PlateRule[] {
  return PLATE_RULES.filter(
    r =>
      r.plateSize.width === plateSize.width &&
      r.plateSize.height === plateSize.height &&
      r.plateSize.layout === plateSize.layout
  )
}

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

export interface ValidationResult {
  valid: boolean
  error?: string
  reason?: string
}

export interface PlateInput {
  cityCode: string
  letters?: string
  numbers?: string
}

/**
 * Get allowed combinations for a rule based on city code length
 */
export function getAllowedCombinationsForCityCode(
  rule: PlateRule,
  cityCodeLength: number
): LetterNumberCombination[] {
  // If rule has city-code-dependent combinations, use those
  if (rule.cityCodeDependentCombinations) {
    if (cityCodeLength === 1 && rule.cityCodeDependentCombinations.cityCodeLength1) {
      return rule.cityCodeDependentCombinations.cityCodeLength1
    }
    if (cityCodeLength === 2 && rule.cityCodeDependentCombinations.cityCodeLength2) {
      return rule.cityCodeDependentCombinations.cityCodeLength2
    }
    if (cityCodeLength === 3 && rule.cityCodeDependentCombinations.cityCodeLength3) {
      return rule.cityCodeDependentCombinations.cityCodeLength3
    }
    return [] // No combinations for this city code length
  }

  // Otherwise use simple allowed combinations
  return rule.allowedCombinations || []
}

/**
 * Validate if a character can be entered in city code section
 */
export function canEnterCityChar(
  rule: PlateRule | null,
  currentInput: PlateInput
): ValidationResult {
  if (!rule) {
    return { valid: false, error: 'No rule found for this configuration' }
  }

  const currentCityLength = currentInput.cityCode.length

  // Check max city code length - allow up to maxCityCodeLength (not exceeding it)
  if (currentCityLength > rule.maxCityCodeLength) {
    return {
      valid: false,
      error: `City code cannot exceed ${rule.maxCityCodeLength} characters`,
    }
  }

  // Check total length would not exceed max
  // Use currentCityLength (not +1) because this is called AFTER the character is added
  const lettersLength = currentInput.letters?.length || 0
  const numbersLength = currentInput.numbers?.length || 0
  const totalLength = currentCityLength + lettersLength + numbersLength

  if (totalLength > rule.maxTotalChars) {
    return {
      valid: false,
      error: `Total length cannot exceed ${rule.maxTotalChars} characters`,
    }
  }

  return { valid: true }
}

/**
 * Validate if a character can be entered in letters section
 */
export function canEnterLetter(
  rule: PlateRule | null,
  currentInput: PlateInput
): ValidationResult {
  if (!rule) {
    return { valid: false, error: 'No rule found for this configuration' }
  }

  // City code must be entered first
  if (!currentInput.cityCode || currentInput.cityCode.length === 0) {
    return { valid: false, error: 'City code must be entered first' }
  }

  const currentLettersLength = currentInput.letters?.length || 0
  const currentNumbersLength = currentInput.numbers?.length || 0

  // Check if we can add another letter
  const newLettersLength = currentLettersLength + 1

  // Check if this combination is allowed
  const cityLength = currentInput.cityCode.length
  const totalLength = cityLength + newLettersLength + currentNumbersLength

  if (totalLength > rule.maxTotalChars) {
    return {
      valid: false,
      error: `Total length cannot exceed ${rule.maxTotalChars} characters`,
    }
  }

  // Get allowed combinations for this city code length
  const combinations = getAllowedCombinationsForCityCode(rule, cityLength)

  if (combinations.length === 0) {
    return {
      valid: false,
      error: `No combinations allowed for ${cityLength}-letter city code`,
    }
  }

  // Check if the combination (newLettersLength, currentNumbersLength) is allowed
  const maxAllowedLetters = Math.max(...combinations.map(c => c.letters))
  if (newLettersLength > maxAllowedLetters) {
    return {
      valid: false,
      error: `Maximum ${maxAllowedLetters} letters allowed`,
    }
  }

  // If numbers are already entered, check if this specific combination is allowed
  if (currentNumbersLength > 0) {
    const combination = combinations.find(
      c => c.letters === newLettersLength && c.numbers === currentNumbersLength
    )
    if (!combination) {
      return {
        valid: false,
        error: `Combination of ${newLettersLength} letters and ${currentNumbersLength} numbers is not allowed for ${cityLength}-letter city code`,
      }
    }
  }

  return { valid: true }
}

/**
 * Validate if a character can be entered in numbers section
 */
export function canEnterNumber(
  rule: PlateRule | null,
  currentInput: PlateInput
): ValidationResult {
  if (!rule) {
    return { valid: false, error: 'No rule found for this configuration' }
  }

  // City code must be entered first
  if (!currentInput.cityCode || currentInput.cityCode.length === 0) {
    return { valid: false, error: 'City code must be entered first' }
  }

  const currentLettersLength = currentInput.letters?.length || 0
  const currentNumbersLength = currentInput.numbers?.length || 0

  // Check if we can add another number
  const newNumbersLength = currentNumbersLength + 1

  // Check total length
  const cityLength = currentInput.cityCode.length
  const totalLength = cityLength + currentLettersLength + newNumbersLength

  if (totalLength > rule.maxTotalChars) {
    return {
      valid: false,
      error: `Total length cannot exceed ${rule.maxTotalChars} characters`,
    }
  }

  // Get allowed combinations for this city code length
  const combinations = getAllowedCombinationsForCityCode(rule, cityLength)

  if (combinations.length === 0) {
    return {
      valid: false,
      error: `No combinations allowed for ${cityLength}-letter city code`,
    }
  }

  // Check if this combination is allowed
  const maxAllowedNumbers = Math.max(...combinations.map(c => c.numbers))
  if (newNumbersLength > maxAllowedNumbers) {
    return {
      valid: false,
      error: `Maximum ${maxAllowedNumbers} numbers allowed`,
    }
  }

  // Check if the specific combination is allowed
  if (currentLettersLength > 0) {
    const combination = combinations.find(
      c => c.letters === currentLettersLength && c.numbers === newNumbersLength
    )
    if (!combination) {
      return {
        valid: false,
        error: `Combination of ${currentLettersLength} letters and ${newNumbersLength} numbers is not allowed for ${cityLength}-letter city code`,
      }
    }
  }

  return { valid: true }
}

/**
 * Validate complete plate input
 */
export function validatePlateInput(
  rule: PlateRule | null,
  input: PlateInput
): ValidationResult {
  if (!rule) {
    return { valid: false, error: 'No rule found for this configuration' }
  }

  // Validate city code
  if (!input.cityCode || input.cityCode.length < rule.minCityCodeLength) {
    return {
      valid: false,
      error: `City code must have at least ${rule.minCityCodeLength} character(s)`,
    }
  }

  if (input.cityCode.length > rule.maxCityCodeLength) {
    return {
      valid: false,
      error: `City code cannot exceed ${rule.maxCityCodeLength} characters`,
    }
  }

  // Validate letters
  const lettersLength = input.letters?.length || 0
  if (lettersLength === 0) {
    return {
      valid: false,
      error: 'At least one letter is required after city code',
    }
  }

  // Validate numbers
  const numbersLength = input.numbers?.length || 0
  if (numbersLength === 0) {
    return {
      valid: false,
      error: 'At least one number is required',
    }
  }

  // Get allowed combinations for this city code length
  const combinations = getAllowedCombinationsForCityCode(rule, input.cityCode.length)

  if (combinations.length === 0) {
    return {
      valid: false,
      error: `No combinations allowed for ${input.cityCode.length}-letter city code`,
    }
  }

  // Check if combination is allowed
  const combination = combinations.find(
    c => c.letters === lettersLength && c.numbers === numbersLength
  )

  if (!combination) {
    return {
      valid: false,
      error: `Combination of ${lettersLength} letters and ${numbersLength} numbers is not allowed for ${input.cityCode.length}-letter city code in this plate configuration`,
    }
  }

  // Check total length
  const totalLength = input.cityCode.length + lettersLength + numbersLength
  if (totalLength > rule.maxTotalChars) {
    return {
      valid: false,
      error: `Total length (${totalLength}) exceeds maximum (${rule.maxTotalChars})`,
    }
  }

  return { valid: true }
}

/**
 * Get allowed letter counts for current input state
 */
export function getAllowedLetterCounts(
  rule: PlateRule | null,
  cityCodeLength: number,
  numbersLength: number
): number[] {
  if (!rule) return []

  // Get combinations for this city code length
  const combinations = getAllowedCombinationsForCityCode(rule, cityCodeLength)

  // Filter combinations that work with current city code and numbers length
  const validCombinations = combinations.filter(c => {
    const totalLength = cityCodeLength + c.letters + numbersLength
    return totalLength <= rule.maxTotalChars
  })

  // Return unique letter counts
  return [...new Set(validCombinations.map(c => c.letters))].sort()
}

/**
 * Get allowed number counts for current input state
 */
export function getAllowedNumberCounts(
  rule: PlateRule | null,
  cityCodeLength: number,
  lettersLength: number
): number[] {
  if (!rule) return []

  // Get combinations for this city code length
  const combinations = getAllowedCombinationsForCityCode(rule, cityCodeLength)

  // Filter combinations that work with current city code and letters length
  const validCombinations = combinations.filter(c => {
    const totalLength = cityCodeLength + lettersLength + c.numbers
    return totalLength <= rule.maxTotalChars
  })

  // Return unique number counts
  return [...new Set(validCombinations.map(c => c.numbers))].sort()
}

// ============================================================================
// FORBIDDEN LETTER COMBINATIONS
// ============================================================================

/**
 * Letter combinations forbidden on German license plates.
 * Banned due to Nazi associations.
 * Applies ONLY to the custom letters section, NOT to city codes.
 */
export const FORBIDDEN_LETTER_COMBINATIONS = [
  'HJ', // Hitlerjugend
  'KZ', // Konzentrationslager
  'NS', // Nationalsozialismus
  'SA', // Sturmabteilung
  'SS', // Schutzstaffel
  'SD', // Sicherheitsdienst
  'AH', // Adolf Hitler
  'HH', // Heil Hitler
]

/**
 * Check if a letter combination is forbidden.
 * Returns error message if forbidden, null if allowed.
 */
export function isForbiddenLetterCombination(letters: string): string | null {
  if (!letters || letters.length < 2) return null
  const upper = letters.toUpperCase()
  if (FORBIDDEN_LETTER_COMBINATIONS.includes(upper)) {
    return `Die Buchstabenkombination "${upper}" ist nicht zulässig`
  }
  return null
}

// ============================================================================
// DIRECT VALIDATION (validates current value, no +1)
// ============================================================================

/**
 * Validate the current letters value directly.
 * Unlike canEnterLetter (which checks if ONE MORE can be added),
 * this validates the given input as-is.
 */
export function validateLettersValue(
  rule: PlateRule | null,
  input: PlateInput
): ValidationResult {
  if (!rule) return { valid: false, error: 'Keine Regel gefunden' }

  const lettersLen = input.letters?.length || 0
  if (lettersLen === 0) return { valid: true }

  // Check forbidden combinations
  if (lettersLen >= 2 && input.letters) {
    const forbidden = isForbiddenLetterCombination(input.letters)
    if (forbidden) return { valid: false, error: forbidden }
  }

  // Need city code first
  const cityLen = input.cityCode?.length || 0
  if (cityLen < rule.minCityCodeLength) {
    return { valid: false, error: 'Bitte zuerst Stadtcode eingeben' }
  }

  // Get allowed combos for this city code length
  const combos = getAllowedCombinationsForCityCode(rule, cityLen)
  if (combos.length === 0) {
    return { valid: false, error: `Keine Kombinationen für ${cityLen}-stelligen Stadtcode` }
  }

  // Check max letters
  const maxLetters = Math.max(...combos.map(c => c.letters))
  if (lettersLen > maxLetters) {
    return { valid: false, error: `Maximal ${maxLetters} Buchstabe(n) erlaubt` }
  }

  // Check total length
  const numbersLen = input.numbers?.length || 0
  if (cityLen + lettersLen + numbersLen > rule.maxTotalChars) {
    return { valid: false, error: `Gesamtlänge darf ${rule.maxTotalChars} Zeichen nicht überschreiten` }
  }

  // If numbers already entered, check specific combo exists
  if (numbersLen > 0) {
    const valid = combos.some(c => c.letters === lettersLen && c.numbers === numbersLen)
    if (!valid) {
      return { valid: false, error: `Kombination ${lettersLen} Buchstabe(n) + ${numbersLen} Ziffer(n) nicht erlaubt` }
    }
  }

  return { valid: true }
}

/**
 * Validate the current numbers value directly.
 * Unlike canEnterNumber (which checks if ONE MORE can be added),
 * this validates the given input as-is.
 */
export function validateNumbersValue(
  rule: PlateRule | null,
  input: PlateInput
): ValidationResult {
  if (!rule) return { valid: false, error: 'Keine Regel gefunden' }

  const numbersLen = input.numbers?.length || 0
  if (numbersLen === 0) return { valid: true }

  const cityLen = input.cityCode?.length || 0
  if (cityLen < rule.minCityCodeLength) {
    return { valid: false, error: 'Bitte zuerst Stadtcode eingeben' }
  }

  const lettersLen = input.letters?.length || 0
  if (lettersLen === 0) {
    return { valid: false, error: 'Bitte zuerst Buchstaben eingeben' }
  }

  // Get allowed combos
  const combos = getAllowedCombinationsForCityCode(rule, cityLen)
  if (combos.length === 0) {
    return { valid: false, error: `Keine Kombinationen für ${cityLen}-stelligen Stadtcode` }
  }

  // Check max numbers for this letters count
  const matchingCombos = combos.filter(c => c.letters === lettersLen)
  if (matchingCombos.length === 0) {
    return { valid: false, error: `${lettersLen} Buchstabe(n) mit diesem Stadtcode nicht erlaubt` }
  }

  const maxNumbers = Math.max(...matchingCombos.map(c => c.numbers))
  if (numbersLen > maxNumbers) {
    return { valid: false, error: `Maximal ${maxNumbers} Ziffer(n) erlaubt` }
  }

  // Check total length
  if (cityLen + lettersLen + numbersLen > rule.maxTotalChars) {
    return { valid: false, error: `Gesamtlänge darf ${rule.maxTotalChars} Zeichen nicht überschreiten` }
  }

  return { valid: true }
}

/**
 * Get max allowed letters for a given city code length
 */
export function getMaxLettersForCityCode(
  rule: PlateRule | null,
  cityCodeLength: number
): number {
  if (!rule) return 2
  const combos = getAllowedCombinationsForCityCode(rule, cityCodeLength)
  if (combos.length === 0) return 0
  return Math.max(...combos.map(c => c.letters))
}

/**
 * Get max allowed numbers for given city code + letters combo
 */
export function getMaxNumbersForCombo(
  rule: PlateRule | null,
  cityCodeLength: number,
  lettersLength: number
): number {
  if (!rule) return 4
  const combos = getAllowedCombinationsForCityCode(rule, cityCodeLength)
  const matching = combos.filter(c => c.letters === lettersLength)
  if (matching.length === 0) return 0
  return Math.max(...matching.map(c => c.numbers))
}

// ============================================================================
// CONFIGURATION GETTERS FOR UI HELPERS
// ============================================================================

/**
 * Returns a list of unique sizes allowed for a given vehicle category
 */
export function getPlateSizesForCategory(category: VehicleCategory): PlateSize[] {
  const rules = PLATE_RULES.filter((r) => r.vehicleCategory === category)

  const uniqueSizes: PlateSize[] = []
  const seenStr = new Set<string>()

  for (const r of rules) {
    const key = `${r.plateSize.width}x${r.plateSize.height}-${r.plateSize.layout}`
    if (!seenStr.has(key)) {
      seenStr.add(key)
      uniqueSizes.push(r.plateSize)
    }
  }

  // Sort: double line first (if they exist), then longest width
  return uniqueSizes.sort((a, b) => {
    if (a.layout !== b.layout) return a.layout === 'zweizeilig' ? -1 : 1
    return b.width - a.width
  })
}

/**
 * Returns a list of unique plate types allowed for a given vehicle category
 */
export function getPlateTypesForCategory(category: VehicleCategory): PlateType[] {
  const rules = PLATE_RULES.filter((r) => r.vehicleCategory === category)
  const types = new Set<PlateType>(rules.map(r => r.plateType))

  // Custom sorting so REGULAR is first
  const order: Record<string, number> = {
    'REGULAR': 0, 'E': 1, 'H': 2, 'REGULAR_SEASON': 3, 'E_SEASON': 4, 'H_SEASON': 5,
    'GREEN': 7, 'SHORT_TERM': 8, 'EXPORT': 9
  }

  return Array.from(types).sort((a, b) => (order[a] ?? 99) - (order[b] ?? 99))
}

/**
 * Finds all rules matching a specific width/height/type setup
 */
export function getRulesForDimensions(
  width: number,
  height: number,
  isSeasonal: boolean,
  isE: boolean,
  isH: boolean
): PlateRule[] {
  let matchedType: PlateType = 'REGULAR'
  if (isE && isSeasonal) matchedType = 'E_SEASON'
  else if (isE) matchedType = 'E'
  else if (isH && isSeasonal) matchedType = 'H_SEASON'
  else if (isH) matchedType = 'H'
  else if (isSeasonal) matchedType = 'REGULAR_SEASON'

  return PLATE_RULES.filter(r =>
    r.plateSize.width === width &&
    r.plateSize.height === height &&
    (r.plateType === matchedType || r.plateType === 'REGULAR_SEASON' && isSeasonal)
  )
}
